import clsx from 'clsx';
import React from 'react';
import LoadingSpinner from '../loading-spinner/loading-spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'cta' | 'warning' | 'ghost' | 'text';
export type ButtonSize = 'default' | 'small' | 'xsmall';

export type ButtonProps = {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  disabled?: boolean;
  size?: ButtonSize;
  href?: string;
  target?: '_blank' | '_self';
  onClick?: (e?: any) => any;
  type?: 'button' | 'submit';
  fullWidth?: boolean;
  className?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  isLoading?: boolean;
  download?: boolean;
  dataReadonlyControl?: string;
};

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'secondary',
  size = 'default',
  disabled,
  href,
  target = '_self',
  onClick,
  type = 'button',
  className,
  fullWidth,
  iconLeft,
  iconRight,
  isLoading,
  download = false,
  dataReadonlyControl,
}) => {
  const buttonClasses = clsx(
    'font-sans rounded-md text-center inline-flex gap-2 items-center justify-center relative',
    variant !== 'text' &&
      (size === 'xsmall'
        ? 'py-1 px-2 font-regular text-base'
        : size === 'small'
        ? 'py-2 px-5 font-regular text-base'
        : 'py-4 px-6 font-bold text-md'),
    fullWidth && 'w-full justify-center',
    variant === 'primary' &&
      clsx(
        !disabled && !isLoading
          ? '!bg-primary border border-primary text-white hover:!bg-primary-300 hover:text-white hover:border-primary-300 active:!bg-primary-400 active:border-primary-400 focus:!text-white'
          : disabled
          ? 'bg-grey-300 border border-grey-300 text-grey-500 cursor-not-allowed pointer-events-none'
          : 'bg-primary-200 text-white text-opacity-0 border border-primary-200 cursor-wait' // Loading styles
      ),
    variant === 'secondary' &&
      clsx(
        !disabled && !isLoading
          ? 'bg-white border !border-primary text-black hover:bg-grey-100 hover:text-black active:bg-primary-100'
          : disabled
          ? 'bg-grey-100 border border-solid border-grey-200 text-grey-400 cursor-not-allowed pointer-events-none'
          : 'bg-white text-white text-opacity-0 border border-primary-200 cursor-wait' // Loading styles
      ),
    variant === 'cta' &&
      clsx(
        !disabled && !isLoading
          ? 'bg-secondary border border-secondary !text-white hover:bg-secondary-300 hover:border-secondary-300 active:bg-secondary-400 active:border-secondary-400'
          : disabled
          ? 'bg-grey-300 border border-grey-300 text-grey-500 cursor-not-allowed pointer-events-none'
          : 'bg-secondary text-white text-opacity-0 border border-secondary cursor-wait' // Loading styles
      ),
    variant === 'warning' &&
      clsx(
        !disabled && !isLoading
          ? 'bg-white border !border-red text-red hover:bg-red hover:text-white active:bg-red-light'
          : disabled
          ? 'bg-grey-100 border border-solid border-grey-200 text-grey-400 cursor-not-allowed pointer-events-none'
          : 'bg-white text-white text-opacity-0 border border-red cursor-wait' // Loading styles
      ),
    variant === 'ghost' &&
      clsx(
        !disabled && !isLoading
          ? 'bg-white text-black hover:bg-grey-100 hover:text-black active:bg-grey-200'
          : disabled
          ? 'bg-grey-100 border border-solid border-grey-200 text-grey-400 cursor-not-allowed pointer-events-none'
          : 'bg-white text-white text-opacity-0 border border-red cursor-wait' // Loading styles
      ),
    variant === 'text' &&
      clsx(
        'bg-transparent p-0 text-green-dark hover:underline',
        disabled && 'text-grey-400 cursor-not-allowed pointer-events-none'
      )
  );

  if (href) {
    return (
      <a
        href={href}
        target={target}
        {...(download ? { download: true } : {})}
        className={clsx(buttonClasses, 'inline-block !no-underline', className)}
        data-readonly-control={dataReadonlyControl}
      >
        {iconLeft && <div>{iconLeft}</div>}
        {children}
        {iconRight && <div>{iconRight}</div>}
      </a>
    );
  }

  const handleButtonClick = (e: any) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      className={clsx(buttonClasses, className)}
      disabled={disabled}
      onClick={handleButtonClick}
      type={type}
      data-readonly-control={dataReadonlyControl}
    >
      {iconLeft && <div>{iconLeft}</div>}
      {children}
      {iconRight && <div>{iconRight}</div>}
      {isLoading && (
        <div className="absolute inset-[0] flex items-center justify-center">
          <LoadingSpinner
            size={size === 'small' ? 'small' : 'medium'}
            color={
              variant === 'cta'
                ? 'fill-secondary-100'
                : variant === 'secondary'
                ? 'fill-primary-300'
                : variant === 'warning'
                ? 'fill-red'
                : 'fill-primary-100'
            }
            backgroundColor={
              variant === 'cta'
                ? 'fill-secondary-300'
                : variant === 'secondary'
                ? 'fill-grey-300'
                : variant === 'warning'
                ? 'fill-grey-300'
                : 'fill-primary-300'
            }
          />
        </div>
      )}
    </button>
  );
};

export default Button;
